import React, { useCallback, useEffect, useRef, useState } from "react";
import { theme } from "antd";

/**
 * Drag-to-resize column widths for the report table.
 *
 * Written rather than pulled in: `react-resizable` is a dependency, a wrapper
 * component and a stylesheet for what is fundamentally a pointerdown, a
 * pointermove and some arithmetic. This is that arithmetic.
 *
 * Three details that make it feel right rather than merely work:
 *
 * * **The drag starts from the width the column already has on screen**, read
 *   off the DOM. Columns begin life auto-sized — no invented default that
 *   snaps the layout the first time someone touches a handle.
 * * **Pointer capture, not window listeners.** The header keeps receiving moves
 *   even when the cursor outruns the column, and the browser cancels the drag
 *   for us if focus is lost. No stuck-in-drag state.
 * * **Widths are remembered per report.** Someone who narrows Description to
 *   read Status expects it that way tomorrow; re-dragging it every visit is the
 *   kind of small tax that makes a tool feel disposable.
 */

const MIN_WIDTH = 64;
const STORAGE_PREFIX = "reports:columnWidths:";

export type ColumnWidths = Record<string, number>;

export function useColumnWidths(reportId: string | undefined) {
  const storageKey = reportId ? `${STORAGE_PREFIX}${reportId}` : "";
  const [widths, setWidths] = useState<ColumnWidths>({});
  // Lets the stable callbacks below read the current widths without being
  // rebuilt (and re-rendering every header cell) on each drag frame.
  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  // Read on mount (and whenever the report changes) rather than in the initial
  // state, so opening a second report in another tab doesn't inherit the first.
  useEffect(() => {
    if (!storageKey) return;
    try {
      const stored = window.localStorage.getItem(storageKey);
      setWidths(stored ? JSON.parse(stored) : {});
    } catch {
      setWidths({});
    }
  }, [storageKey]);

  const persist = useCallback(
    (next: ColumnWidths) => {
      setWidths(next);
      if (!storageKey) return;
      try {
        // A quota error here must not take the table down with it.
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* private mode, quota — the widths just don't outlive the session */
      }
    },
    [storageKey]
  );

  const setWidth = useCallback(
    (key: string, width: number) =>
      persist({ ...widthsRef.current, [key]: Math.round(width) }),
    [persist]
  );

  const clearWidth = useCallback(
    (key: string) => {
      const next = { ...widthsRef.current };
      delete next[key];
      persist(next);
    },
    [persist]
  );

  const reset = useCallback(() => persist({}), [persist]);

  return {
    widths,
    setWidth,
    clearWidth,
    reset,
    customised: Object.keys(widths).length > 0,
  };
}

type CellProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  /** Column key; absent on antd's internal cells (selection, expand). */
  columnKey?: string;
  onResize?: (key: string, width: number) => void;
  /** Double-click the handle to hand the column back to auto-sizing. */
  onResetColumn?: (key: string) => void;
};

/**
 * A header cell with a grab handle on its trailing edge.
 *
 * Falls back to a plain `<th>` for any cell without a column key, so antd's own
 * internal columns keep working untouched.
 */
export const ResizableHeaderCell: React.FC<CellProps> = ({
  columnKey,
  onResize,
  onResetColumn,
  children,
  ...rest
}) => {
  const { token } = theme.useToken();
  const cellRef = useRef<HTMLTableCellElement>(null);
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);
  const lastWidth = useRef<number | null>(null);
  const [dragging, setDragging] = useState(false);

  if (!columnKey || !onResize) {
    return <th {...rest}>{children}</th>;
  }

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation(); // never let the handle trigger a column sort
    const width = cellRef.current?.getBoundingClientRect().width ?? MIN_WIDTH;
    drag.current = { startX: event.clientX, startWidth: width };
    setDragging(true);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Capture is an optimisation, not a requirement — without it the drag
      // still tracks, it just stops if the cursor outruns the handle.
    }
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const { startX, startWidth } = drag.current;
    const next = Math.max(MIN_WIDTH, startWidth + (event.clientX - startX));
    // Applied straight away rather than throttled through requestAnimationFrame:
    // rAF does not fire in a backgrounded or occluded tab, which turns a
    // "throttle" into a drag that silently does nothing. React coalesces the
    // updates anyway, and a page of 25 rows keeps up comfortably.
    if (next !== lastWidth.current) {
      lastWidth.current = next;
      onResize(columnKey, next);
    }
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    lastWidth.current = null;
    setDragging(false);
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      /* capture was never taken */
    }
  };

  return (
    <th {...rest} ref={cellRef} style={{ ...rest.style, position: "relative" }}>
      {children}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize column"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={(event) => {
          event.stopPropagation();
          onResetColumn?.(columnKey);
        }}
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "absolute",
          top: 0,
          right: -4,
          bottom: 0,
          // Wider than it looks: a 1px target is a game, not a control.
          width: 12,
          cursor: "col-resize",
          zIndex: 2,
          touchAction: "none",
          display: "flex",
          justifyContent: "center",
          alignItems: "stretch",
        }}
      >
        <span
          style={{
            width: 2,
            background: dragging ? token.colorPrimary : "transparent",
            transition: dragging ? undefined : "background .12s",
          }}
          // The rule only appears under the cursor, so a header of six columns
          // isn't a picket fence.
          onMouseEnter={(event) => {
            if (!dragging)
              (event.currentTarget as HTMLElement).style.background =
                token.colorBorder;
          }}
          onMouseLeave={(event) => {
            if (!dragging)
              (event.currentTarget as HTMLElement).style.background = "transparent";
          }}
        />
      </div>
    </th>
  );
};
